// Tiny fetch wrapper used by every screen. Throws on non-2xx with the
// server's error message so callers can `try/catch` straightforwardly.
//
// v0.17.3 — apiSend now detects network-down failures and enqueues
// the mutation into the IndexedDB outbox. The call resolves with a
// `{ queued: true }` marker so callers can show optimistic UI without
// needing to know about the queue. The outbox drains on reconnect
// via OfflineBanner.

import { addToOutbox } from './utils/outbox.js';
// v0.171.0 — SITE MODE. Site-capture writes go straight to the outbox when the
// device is set to hold; see utils/syncMode.js.
import { isHoldMode } from './utils/syncMode.js';
// v0.172.0 — a GET after a change asks the network first; the service worker
// reports where an answer came from. See utils/apiFreshness.js.
import { markApiMutated, needsFresh, noteFresh, listenToServiceWorker } from './utils/apiFreshness.js';
// 2026-10-19 — a 401 that is the sign-in ending (not another service refusing)
// takes the person through the suite sign-in instead of an error box.
import { sessionHasEnded } from './utils/sessionEnd.js';
listenToServiceWorker();

// The writes that are "site capture" — the work done standing on a plan. In
// hold mode these never touch the network; everything else (sharing a report,
// inviting a contact, account settings) still needs the server to answer and
// keeps today's send-then-queue-on-failure behaviour.
const HOLDABLE = [
  /^\/api\/defects(\/|\?|$)/,
  /^\/api\/defect-photos(\/|\?|$)/,
  /^\/api\/defect-comments(\/|\?|$)/,
];
export function isHoldablePath(path) {
  const p = String(path || '');
  return HOLDABLE.some((re) => re.test(p));
}

// v0.109.0 — retry a GET once when the PLATFORM fails, not when the app does.
//
// The saved-reports screen showed
//   GET /api/reports?projectId=74 → 500: Backend call failure
// while that exact request returned both rows correctly a moment later. The
// endpoint was never broken: "Backend call failure" is Static Web Apps' own
// plain-text body for a managed function it could not invoke — a cold start
// after a deploy, or a host recycle. Our own handlers never produce it; they
// always answer with JSON.
//
// A GET is idempotent, so one retry is safe, and the alternative is what
// happened here: a momentary platform blip leaves a screen looking permanently
// broken until someone reloads by hand.
//
// Deliberately narrow:
//   • GET only. Mutations must never be retried automatically — a POST that
//     actually succeeded before the connection dropped would be duplicated.
//   • 5xx and network failures only. A 401/403/404 is a real answer about the
//     request and retrying it just doubles the work before the same error.
//   • Once. A persistent outage should surface, not be hidden behind a
//     spinner that retries forever.
const RETRY_DELAY_MS = 700;

//
// v0.172.0 — `opts.fresh` (or a change made since this path last came fresh
// from the network) sends X-DropPin-Fresh, which the service worker honours
// as network-first instead of serving its cached copy. Without it a screen
// opened right after a save showed the pre-save cache (Brett: "after I load
// some pins they don't appear on the defects list straight away").
export async function apiGet(path, opts) {
  const fresh = !!(opts && opts.fresh) || needsFresh(path);
  const init = fresh ? { headers: { 'X-DropPin-Fresh': '1' } } : undefined;
  let r;
  try {
    r = await fetch(path, init);
  } catch (e) {
    // Network-level failure: nothing came back at all.
    await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
    r = await fetch(path, init);
  }

  if (r.status >= 500) {
    await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
    r = await fetch(path, init);
  }

  // The service worker stamps what it served; no stamp means no service
  // worker in the way (dev, native shell), i.e. the network answered.
  if (r.ok && r.headers.get('x-droppin-source') !== 'cache') noteFresh(path);

  if (!r.ok) {
    const body = await r.text();
    const err = new Error(`GET ${path} → ${r.status}: ${body || r.statusText}`);
    // 2026-08-17 — carry the STATUS, not just prose. See apiSend below.
    err.status = r.status;
    err.path = path;
    // v0.295.0 — and the parsed body, as apiSend has carried since v0.207.2. A
    // screen that names its states reads the API's `code` (no-seat, unreachable,
    // not-configured…) off it instead of matching the sentence in the message.
    try { const j = JSON.parse(body); if (j && typeof j === 'object') err.body = j; } catch {}
    if (r.status === 401 && await sessionHasEnded(path)) err.sessionEnded = true;
    throw err;
  }
  return r.json();
}

// v0.295.0 — `opts.noQueue`: a write that must happen NOW or not at all.
//
// Every failed write is queued to the outbox and answered { queued: true } —
// right for a pin made on site, wrong for one that answers a question about
// the moment it was asked. The InSpec import plans against the contacts as
// they stand; replayed an hour later from the outbox it would add people the
// screen never showed, and the screen would have said nothing was wrong. With
// noQueue a network failure THROWS (err.offline = true) and hold mode does not
// apply: nothing waits, the caller says "nothing was imported".
export async function apiSend(method, path, body, opts) {
  const noQueue = !!(opts && opts.noQueue);
  // v0.171.0 — hold mode: queue and answer at once, no network attempt. On one
  // bar of reception a request does not fail, it hangs; Save must not wait on
  // it. Callers already handle the { queued: true } marker (the offline path
  // has answered with it since v0.17.3).
  if (!noQueue && method !== 'GET' && isHoldMode() && isHoldablePath(path)) {
    const localId = await addToOutbox(method, path, body);
    return { queued: true, localId, held: true };
  }
  let r;
  try {
    r = await fetch(path, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    // v0.17.3 — Network down (DNS fail / connection refused / etc).
    // Queue the mutation and resolve with a marker. Only queue
    // mutations — GETs are useless when offline (we have no cache
    // semantics here for arbitrary GET; the SW cache covers reads).
    if (noQueue) {
      const err = new Error(`${method} ${path} → no connection`);
      err.offline = true;
      err.path = path;
      throw err;
    }
    if (method !== 'GET') {
      const localId = await addToOutbox(method, path, body);
      return { queued: true, localId, offline: true };
    }
    throw e;
  }
  if (!r.ok) {
    const txt = await r.text();
    let msg = `${method} ${path} → ${r.status}`;
    try { const j = JSON.parse(txt); if (j?.error) msg = j.error; } catch {}
    // 2026-08-17 — CARRY THE STATUS ON THE ERROR.
    //
    // This threw a bare Error whose message was the server's own sentence, and
    // screens then printed that sentence at the user. It reads fine at the API
    // — /api/import-locations answers 401 "Not signed in." — and wrong
    // everywhere else: Brett got "⚠ Not signed in." across the top of his
    // Locations page while signed in, looking at his locations, with his name
    // in the header. The failing thing was the InSite site sync, not his
    // session, and the banner said the one thing that was not true.
    //
    // A caller cannot tell 401 from 500 by reading prose without string-
    // matching it, which breaks the moment the wording changes. So the status
    // rides along and screens decide what to say (see utils/syncMessage.js).
    const err = new Error(msg);
    err.status = r.status;
    err.path = path;
    // v0.207.1 — AND THE DETAIL, when the API sends one. A handler's catch-all
    // sentence ("Could not save the video") is written for the person; `detail`
    // is the reason underneath it, and without carrying it here a screen can
    // only ever show the sentence. Brett hit exactly that: every clip failed
    // with the same line and there was no way, from the phone or from the
    // code, to tell a broken query from a storage outage.
    // v0.207.2 — and the whole body with it. `detail` was the first field a
    // caller needed off a failed response; `blobMissing` is the second, and
    // adding a line per field is how this ends up carrying eight of them. The
    // parsed body rides along as `err.body` and the two that callers read by
    // name are lifted out of it.
    try {
      const j = JSON.parse(txt);
      if (j && typeof j === 'object') {
        err.body = j;
        if (j.detail) err.detail = String(j.detail);
        if (j.blobMissing) err.blobMissing = true;
      }
    } catch {}
    if (r.status === 401 && await sessionHasEnded(path)) err.sessionEnded = true;
    throw err;
  }
  if (r.status === 204) return null;
  // v0.172.0 — the data changed; the next list a screen asks for must not be
  // the cached one.
  markApiMutated();
  return r.json();
}

export function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return ''; }
}
